import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Play, Eye, Bell, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEmailSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API}/subscription/email`, { email });
      toast.success('Successfully subscribed to email notifications!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Navbar />
      
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(2, 6, 23, 0.85), rgba(2, 6, 23, 0.85)), url('https://images.unsplash.com/photo-1554941426-a965fb2b9258?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxuZXdzJTIwc3R1ZGlvJTIwbWljcm9waG9uZSUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NjUxMjU3MTB8MA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block mb-6 px-4 py-2 border border-red-600 rounded-sm text-red-500 text-sm font-medium" style={{ fontFamily: 'Oswald, sans-serif' }}>
              TRUTH UNFILTERED
            </div>
            <h1 
              data-testid="hero-heading"
              className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
              style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: '-0.02em' }}
            >
              News Through A<br />
              <span className="text-red-600">Christian Lens</span>
            </h1>
            <p 
              className="text-xl sm:text-2xl text-slate-300 mb-12 max-w-3xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Unfiltered Vision News delivers bold, uncompromising coverage of current events from a Christian perspective. No spin. Just truth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/videos" data-testid="explore-videos-button">
                <Button 
                  size="lg" 
                  className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 rounded-sm font-bold"
                  style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                  <Play className="w-5 h-5 mr-2" />
                  Explore Videos
                </Button>
              </Link>
              <Link to="/pricing" data-testid="subscribe-now-button">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-slate-950 text-lg px-8 py-6 rounded-sm font-bold"
                  style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                  Subscribe Now
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-slate-900" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 
            className="text-4xl sm:text-5xl font-bold text-center mb-16"
            style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
          >
            Why Choose Us
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="p-8 border border-slate-700 rounded-sm hover:border-red-600 hover:shadow-[4px_4px_0px_0px_rgba(220,38,38,0.3)]" 
              style={{ transition: 'border 0.3s, box-shadow 0.3s' }}
            >
              <Eye className="w-12 h-12 text-red-600 mb-4" />
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>UNFILTERED TRUTH</h3>
              <p className="text-slate-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                We present news as it is, without mainstream media bias. Get the facts, form your own opinion.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="p-8 border border-slate-700 rounded-sm hover:border-red-600 hover:shadow-[4px_4px_0px_0px_rgba(220,38,38,0.3)]" 
              style={{ transition: 'border 0.3s, box-shadow 0.3s' }}
            >
              <Shield className="w-12 h-12 text-red-600 mb-4" />
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>CHRISTIAN VALUES</h3>
              <p className="text-slate-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                Every story is analyzed through a Biblical worldview, helping you understand events in light of eternal truth.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="p-8 border border-slate-700 rounded-sm hover:border-red-600 hover:shadow-[4px_4px_0px_0px_rgba(220,38,38,0.3)]" 
              style={{ transition: 'border 0.3s, box-shadow 0.3s' }}
            >
              <Bell className="w-12 h-12 text-red-600 mb-4" />
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>INSTANT UPDATES</h3>
              <p className="text-slate-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                Never miss breaking news. Get instant notifications when we publish new content.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Email Subscription Section */}
      <section className="py-24 bg-slate-950" data-testid="email-subscription-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 
            className="text-4xl sm:text-5xl font-bold mb-6"
            style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
          >
            Stay Informed
          </h2>
          <p className="text-xl text-slate-400 mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
            Subscribe to our email list and get notified when we publish new videos.
          </p>
          <form onSubmit={handleEmailSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <Input
              data-testid="email-input"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 h-12 px-4 rounded-sm"
              required
            />
            <Button 
              data-testid="subscribe-button"
              type="submit" 
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white px-8 h-12 rounded-sm font-bold"
              style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
            >
              {loading ? 'Subscribing...' : 'Subscribe'}
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 text-center text-slate-400">
          <p style={{ fontFamily: 'Inter, sans-serif' }}>
            © 2025 Unfiltered Vision News. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
